import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAnchoredPosition } from '../hooks/useAnchoredPosition';
import {
    toYmd,
    formatDisplayYmd,
    getDecadeRange,
    todayYmd,
    startOfMonthYmd,
    endOfMonthYmd,
    addDaysYmd,
} from '../lib/calendarYmd';
import {
    tableToolbarDatePickerTriggerClass,
    tableToolbarControlOpenClass,
    tableToolbarPlaceholderTextClass,
    tableToolbarValueTextClass,
    tableToolbarIconClass,
    tableToolbarIconOpenClass,
} from '../lib/tableClasses';
import FilterChip from './FilterChip';
import { Icon } from './Icon';
import type { IconName } from './Icon';

const DEFAULT_MONTH_NAMES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DEFAULT_DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DEFAULT_PLACEHOLDER = 'Rango de fechas';
const DEFAULT_CLEAR_LABEL = 'Limpiar';
const DEFAULT_LAST7_LABEL = 'Últimos 7 días';
const DEFAULT_THIS_MONTH_LABEL = 'Este mes';

/** Measure the inner input box (the visible field), not the wrapper div. */
const measureInputBox = (el: HTMLElement): DOMRect =>
    (el.querySelector('.input-container-box') ?? el).getBoundingClientRect();

export interface DateRangePickerProps {
    startDate: string;
    endDate: string;
    onRangeChange: (start: string, end: string) => void;
    /** Use inside `tableToolbarClass`. */
    variant?: 'default' | 'tableToolbar' | 'chip';
    /** `variant="chip"` only — the part before the colon («Fechas»). */
    chipLabel?: string;
    /** `variant="chip"` only — renders a ✕ that calls this. */
    onChipRemove?: () => void;
    chipRemoveLabel?: string;
    /** Open the calendar as soon as it mounts (a filter just added from «+ Filtro»). */
    autoOpen?: boolean;
    /** Told every time the calendar opens or closes. */
    onOpenChange?: (open: boolean) => void;
    align?: 'left' | 'right';
    placeholder?: string;
    /** Localization overrides — default to Spanish. Pass arrays of length 12/7. */
    monthNames?: string[];
    dayNames?: string[];
    clearLabel?: string;
    last7Label?: string;
    thisMonthLabel?: string;
}

/**
 * Two-click range: first click anchors, second completes (order-independent).
 * With popover open, a new first click restarts selection (parent unchanged until pair completes).
 * The clear button clears both. Quick presets in footer.
 *
 * Locale-agnostic: month/day names + footer labels default to Spanish; the tenant
 * wrapper injects translated values via `useLocale`'s `t()` (mirrors CustomDatePicker).
 */
const DateRangePicker: React.FC<DateRangePickerProps> = ({
    startDate,
    endDate,
    onRangeChange,
    variant = 'default',
    align = 'left',
    placeholder = DEFAULT_PLACEHOLDER,
    monthNames = DEFAULT_MONTH_NAMES,
    dayNames = DEFAULT_DAY_NAMES,
    clearLabel = DEFAULT_CLEAR_LABEL,
    last7Label = DEFAULT_LAST7_LABEL,
    thisMonthLabel = DEFAULT_THIS_MONTH_LABEL,
    chipLabel,
    onChipRemove,
    chipRemoveLabel,
    autoOpen = false,
    onOpenChange,
}) => {
    const MONTH_NAMES = monthNames;
    const DAY_NAMES = dayNames;
    const [isOpen, setIsOpen] = useState(autoOpen);
    const onOpenChangeRef = useRef(onOpenChange);
    onOpenChangeRef.current = onOpenChange;
    const openedOnce = useRef(false);
    useEffect(() => {
        if (!openedOnce.current && !isOpen) return;
        openedOnce.current = true;
        onOpenChangeRef.current?.(isOpen);
    }, [isOpen]);
    const [viewDate, setViewDate] = useState(() => new Date());
    const [pickerView, setPickerView] = useState<'days' | 'months' | 'years'>('days');
    /** First day of an in-progress pair (not yet committed to parent). */
    const [anchorYmd, setAnchorYmd] = useState<string | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    // Anchored position with vertical flip + horizontal clamp (shared hook).
    const pos = useAnchoredPosition(rootRef, isOpen, {
        estimatedHeight: 360,
        estimatedWidth: 288,
        align,
        gap: 4,
        measure: measureInputBox,
    });

    useEffect(() => {
        const initial = startDate || endDate || todayYmd();
        const parsed = new Date(initial);
        setViewDate(Number.isNaN(parsed.getTime()) ? new Date() : parsed);
    }, [startDate, endDate]);

    useEffect(() => {
        const onOutside = (event: MouseEvent) => {
            if (!isOpen || !rootRef.current) return;
            const t = event.target as HTMLElement;
            if (rootRef.current.contains(t) || t.closest('.datepicker-range-portal')) return;
            setIsOpen(false);
            setAnchorYmd(null);
        };
        document.addEventListener('mousedown', onOutside);
        return () => document.removeEventListener('mousedown', onOutside);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setIsOpen(false);
                setAnchorYmd(null);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen]);

    const openPopover = () => {
        setPickerView('days');
        setAnchorYmd(null);
        setIsOpen(true);
    };

    const closePopover = () => {
        setIsOpen(false);
        setAnchorYmd(null);
    };

    const handleClear = () => {
        onRangeChange('', '');
        setAnchorYmd(null);
    };

    const applyPresetThisMonth = () => {
        const now = new Date();
        onRangeChange(startOfMonthYmd(now), endOfMonthYmd(now));
        setAnchorYmd(null);
    };

    const applyPresetLast7 = () => {
        const t = todayYmd();
        onRangeChange(addDaysYmd(t, -6), t);
        setAnchorYmd(null);
    };

    const handleDateClick = (year: number, month: number, day: number) => {
        const clicked = toYmd(year, month, day);
        if (anchorYmd === null) {
            setAnchorYmd(clicked);
            return;
        }
        const lo = anchorYmd < clicked ? anchorYmd : clicked;
        const hi = anchorYmd < clicked ? clicked : anchorYmd;
        onRangeChange(lo, hi);
        setAnchorYmd(null);
    };

    const handlePrevMonth = () => setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    const handleNextMonth = () => setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    const handlePrevYear = () => setViewDate(prev => new Date(prev.setFullYear(prev.getFullYear() - 1)));
    const handleNextYear = () => setViewDate(prev => new Date(prev.setFullYear(prev.getFullYear() + 1)));
    const handlePrevDecade = () => setViewDate(prev => new Date(prev.setFullYear(prev.getFullYear() - 10)));
    const handleNextDecade = () => setViewDate(prev => new Date(prev.setFullYear(prev.getFullYear() + 10)));

    const handleMonthClick = (monthIndex: number) => {
        setViewDate(prev => new Date(prev.getFullYear(), monthIndex, 1));
        setPickerView('days');
    };

    const handleYearClick = (year: number) => {
        setViewDate(prev => new Date(prev.setFullYear(year)));
        setPickerView('months');
    };

    /** Effective range for highlighting: committed props + in-progress anchor. */
    const rangeLo =
        anchorYmd === null
            ? startDate && endDate
                ? (startDate < endDate ? startDate : endDate)
                : startDate || null
            : anchorYmd;
    const rangeHi =
        anchorYmd === null
            ? startDate && endDate
                ? (startDate < endDate ? endDate : startDate)
                : endDate || startDate || null
            : anchorYmd;

    const isInRange = (ymd: string): boolean => {
        if (!rangeLo || !rangeHi) return false;
        return ymd >= rangeLo && ymd <= rangeHi;
    };

    const isRangeStart = (ymd: string): boolean => rangeLo === ymd;
    const isRangeEnd = (ymd: string): boolean => rangeHi === ymd;

    /** Range selection follows the tenant's `primary` brand colour (the channel
     *  migration made `bg-primary/NN` opacity work, so the range fill / today
     *  highlight now tint with the brand instead of a fixed blue). */
    function dayCellClass(
        ymd: string,
        inRange: boolean,
        isStart: boolean,
        isEnd: boolean,
        isMuted: boolean,
        isToday = false
    ): string {
        const base =
            'text-center p-1.5 rounded-md cursor-pointer transition-colors text-sm flex items-center justify-center min-h-[2rem]';
        if (isMuted && !inRange) {
            return `${base} text-slate-300 dark:text-slate-600 hover:bg-slate-50 dark:hover:bg-surface-hover-dark/80`;
        }
        if (isStart || isEnd) {
            return `${base} bg-primary dark:bg-primary text-white font-bold shadow-sm z-[1] relative ring-1 ring-primary-dark/40 dark:ring-primary-light/30 ${isMuted ? 'opacity-95' : ''}`;
        }
        if (inRange) {
            return `${base} bg-primary/25 dark:bg-primary/35 text-text-main dark:text-white/95 ${isMuted ? 'opacity-90' : ''}`;
        }
        if (isToday) {
            return `${base} text-primary dark:text-primary-light font-bold bg-primary/10 dark:bg-primary/15 ring-1 ring-inset ring-primary/35 dark:ring-primary-light/40`;
        }
        return `${base} text-text-main dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-surface-hover-dark`;
    }

    const renderDaysView = () => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const prevMonthDate = new Date(year, month, 0);
        const daysInPrevMonth = prevMonthDate.getDate();
        const startDayOfWeek = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
        const cells: React.ReactNode[] = [];

        for (let i = 0; i < startDayOfWeek; i++) {
            const day = daysInPrevMonth - startDayOfWeek + 1 + i;
            const py = prevMonthDate.getFullYear();
            const pm = prevMonthDate.getMonth();
            const ymd = toYmd(py, pm, day);
            const inR = isInRange(ymd);
            const isS = isRangeStart(ymd);
            const isE = isRangeEnd(ymd);
            cells.push(
                <div
                    key={`pad-s-${i}`}
                    onClick={() => handleDateClick(py, pm, day)}
                    className={dayCellClass(ymd, inR, isS, isE, true)}
                >
                    {day}
                </div>
            );
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const ymd = toYmd(year, month, day);
            const inR = isInRange(ymd);
            const isS = isRangeStart(ymd);
            const isE = isRangeEnd(ymd);
            const isToday = todayYmd() === ymd;
            cells.push(
                <div
                    key={day}
                    onClick={() => handleDateClick(year, month, day)}
                    className={dayCellClass(ymd, inR, isS, isE, false, isToday)}
                >
                    {day}
                </div>
            );
        }

        const gridSize = 42;
        const nextMonthDate = new Date(year, month + 1, 1);
        for (let i = 1; i <= gridSize - cells.length; i++) {
            const ny = nextMonthDate.getFullYear();
            const nm = nextMonthDate.getMonth();
            const ymd = toYmd(ny, nm, i);
            const inR = isInRange(ymd);
            const isS = isRangeStart(ymd);
            const isE = isRangeEnd(ymd);
            cells.push(
                <div
                    key={`pad-e-${i}`}
                    onClick={() => handleDateClick(ny, nm, i)}
                    className={dayCellClass(ymd, inR, isS, isE, true)}
                >
                    {i}
                </div>
            );
        }

        return (
            <>
                <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-bold text-text-sub dark:text-slate-400 uppercase tracking-wider mb-2">
                    {DAY_NAMES.map(dn => (
                        <div key={dn}>{dn}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">{cells}</div>
            </>
        );
    };

    const renderMonthsView = () => (
        <div className="grid grid-cols-3 gap-2">
            {MONTH_NAMES.map((month, index) => (
                <button
                    key={month}
                    type="button"
                    onClick={() => handleMonthClick(index)}
                    className={`p-2 rounded-md text-sm transition-colors ${
                        index === viewDate.getMonth()
                            ? 'bg-primary text-white font-bold shadow-sm'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-surface-hover-dark'
                    }`}
                >
                    {month.substring(0, 3)}
                </button>
            ))}
        </div>
    );

    const renderYearsView = () => {
        const { start, end } = getDecadeRange(viewDate.getFullYear());
        const years = Array.from({ length: end - start + 3 }, (_, i) => start - 1 + i);
        const selectedYear = viewDate.getFullYear();
        return (
            <div className="grid grid-cols-4 gap-2">
                {years.map(y => (
                    <button
                        key={y}
                        type="button"
                        onClick={() => handleYearClick(y)}
                        className={`p-2 rounded-md text-sm transition-colors ${
                            y === selectedYear
                                ? 'bg-primary text-white font-bold shadow-sm'
                                : 'hover:bg-slate-50 dark:hover:bg-surface-hover-dark'
                        } ${y < start || y > end ? 'text-slate-400' : 'text-slate-700 dark:text-slate-300'}`}
                    >
                        {y}
                    </button>
                ))}
            </div>
        );
    };

    const renderHeader = () => {
        const year = viewDate.getFullYear();
        const { start, end } = getDecadeRange(year);
        const NavButton: React.FC<{ onClick: () => void; icon: IconName }> = ({ onClick, icon }) => (
            <button
                type="button"
                onClick={onClick}
                className="p-1 rounded-md text-slate-500 hover:text-primary hover:bg-slate-50 dark:hover:bg-surface-hover-dark transition-colors"
            >
                <Icon name={icon} className="!text-[18px]" />
            </button>
        );
        switch (pickerView) {
            case 'years':
                return (
                    <div className="flex items-center justify-between mb-3 px-1">
                        <NavButton onClick={handlePrevDecade} icon="chevron_left" />
                        <span className="font-bold text-sm text-text-main dark:text-white">
                            {start} - {end}
                        </span>
                        <NavButton onClick={handleNextDecade} icon="chevron_right" />
                    </div>
                );
            case 'months':
                return (
                    <div className="flex items-center justify-between mb-3 px-1">
                        <NavButton onClick={handlePrevYear} icon="chevron_left" />
                        <button
                            type="button"
                            onClick={() => setPickerView('years')}
                            className="font-bold text-sm text-text-main dark:text-white hover:text-primary transition-colors"
                        >
                            {year}
                        </button>
                        <NavButton onClick={handleNextYear} icon="chevron_right" />
                    </div>
                );
            default:
                return (
                    <div className="flex items-center justify-between mb-3 px-1">
                        <NavButton onClick={handlePrevMonth} icon="chevron_left" />
                        <button
                            type="button"
                            onClick={() => setPickerView('months')}
                            className="font-bold text-sm text-text-main dark:text-white hover:text-primary transition-colors"
                        >
                            {MONTH_NAMES[viewDate.getMonth()]}{' '}
                            <span className="text-xs ml-1 opacity-80">{year}</span>
                        </button>
                        <NavButton onClick={handleNextMonth} icon="chevron_right" />
                    </div>
                );
        }
    };

    const toolbarTriggerClass = [
        'input-container-box',
        tableToolbarDatePickerTriggerClass,
        isOpen ? tableToolbarControlOpenClass : '',
    ]
        .filter(Boolean)
        .join(' ');

    const defaultTriggerClass = [
        'input-container-box relative w-full h-10 flex items-center justify-between cursor-pointer rounded-md border px-3 transition-all shadow-sm',
        'bg-white dark:bg-surface-dark',
        isOpen
            ? 'border-primary ring-1 ring-primary'
            : 'border-border-strong dark:border-border-strong-dark active:opacity-90',
    ].join(' ');

    return (
        <div
            ref={rootRef}
            className={
                variant === 'chip'
                    ? 'relative inline-flex min-w-0 shrink-0'
                    : `relative w-full min-w-0 flex flex-col justify-center ${variant === 'tableToolbar' ? 'h-10' : ''}`
            }
        >
            {variant === 'chip' ? (
                <FilterChip
                    className="input-container-box"
                    label={chipLabel ?? placeholder}
                    value={
                        startDate && endDate
                            ? `${formatDisplayYmd(startDate)} — ${formatDisplayYmd(endDate)}`
                            : '—'
                    }
                    icon="date_range"
                    active={!!(startDate || endDate)}
                    open={isOpen}
                    onRemove={onChipRemove}
                    removeLabel={chipRemoveLabel}
                    aria-expanded={isOpen}
                    onClick={() => (isOpen ? closePopover() : openPopover())}
                />
            ) : (
            <button
                type="button"
                onClick={() => (isOpen ? closePopover() : openPopover())}
                className={variant === 'tableToolbar' ? toolbarTriggerClass : defaultTriggerClass}
            >
                <span
                    className={`min-w-0 truncate tabular-nums ${
                        startDate && endDate ? tableToolbarValueTextClass : tableToolbarPlaceholderTextClass
                    }`}
                >
                    {startDate && endDate
                        ? `${formatDisplayYmd(startDate)} — ${formatDisplayYmd(endDate)}`
                        : placeholder}
                </span>
                <Icon name="date_range" className={`${tableToolbarIconClass} ${isOpen ? tableToolbarIconOpenClass : ''}`} />
            </button>
            )}
            {isOpen &&
                ReactDOM.createPortal(
                    <div
                        className={`datepicker-range-portal fixed z-[9999] w-72 bg-white dark:bg-surface-dark shadow-xl shadow-slate-200/50 dark:shadow-black/50 rounded-lg border border-border-subtle dark:border-border-subtle-dark p-3 animate-in fade-in zoom-in-95 duration-100 ${pos?.flip ? 'origin-bottom' : 'origin-top'}`}
                        style={{
                            top: pos?.top,
                            bottom: pos?.bottom,
                            left: pos?.left,
                            visibility: pos ? 'visible' : 'hidden',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {renderHeader()}
                        {pickerView === 'days' && renderDaysView()}
                        {pickerView === 'months' && renderMonthsView()}
                        {pickerView === 'years' && renderYearsView()}
                        <div className="mt-3 pt-2 border-t border-border-subtle dark:border-border-subtle-dark flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={handleClear}
                                title={clearLabel}
                                className="text-primary hover:text-primary-dark transition-colors flex items-center justify-center"
                            >
                                <Icon name="cleaning_services" className="!text-[18px]" />
                            </button>
                            <button
                                type="button"
                                onClick={applyPresetLast7}
                                className="text-xs font-semibold text-slate-500 hover:text-primary dark:text-slate-400"
                            >
                                {last7Label}
                            </button>
                            <button
                                type="button"
                                onClick={applyPresetThisMonth}
                                className="text-xs font-semibold text-slate-500 hover:text-primary dark:text-slate-400"
                            >
                                {thisMonthLabel}
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default DateRangePicker;
